import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Drawer, Modal } from "antd";
import Layout from "../../layout/base/Layout";
import { COLORS, Typo } from "../../layout/theme";
import Aside from "../../common/aside/Aside";
import Image from "../../common/image/Image";
import PermissionCheckVisibility from "../../common/permission-check-visibility/PermissionCheckVisibility";
import FooterContainer from "./FooterContainer";
import SellerHeader from "./components/seller-header/SellerHeader";
import SystemLogo from "../../../../src/assets/images/LogoPoloSeguros.png";
import {
	CarOutlined,
	UserOutlined,
	PictureOutlined,
	CheckCircleOutlined,
	CloseCircleOutlined,
} from "@ant-design/icons";
import { BiEdit, BiChevronDown } from "react-icons/bi";
import { BsCardList } from "react-icons/bs";
import { useFilter } from "../../../contexts/FilterContext";
import { useReport } from "../../../contexts/ReportContext";
import { get_car_model_photos, get_car_model_info } from "../../../api/endpoints/cars";
import { useAxiosWithAuthInfoAuto } from "../../../utils/fetchWithAuth";
import { numberWithDotAndCommas } from "../../../utils/numberWithDotAndComas";

/**
 * Parsea un string HTML simple con formato "<ul><li>Item 1</li><li>Item 2</li></ul>"
 * (tal como lo genera el backend a partir del campo "detail" de cada plan) y
 * devuelve un array de strings con el texto de cada item, ya "unescapado".
 */
const parseDetailHtmlToItems = (detailHtml) => {
	if (!detailHtml || typeof detailHtml !== "string") return [];

	const matches = [...detailHtml.matchAll(/<li>([\s\S]*?)<\/li>/g)];

	const unescapeHtml = (str) =>
		str
			.replace(/&amp;/g, "&")
			.replace(/&lt;/g, "<")
			.replace(/&gt;/g, ">")
			.replace(/&quot;/g, '"')
			.replace(/&#39;/g, "'");

	return matches.map((match) => unescapeHtml(match[1].trim())).filter(Boolean);
};


export default function LayoutContainer({ children }) {
	const [showDrawer, setShowDrawer] = useState(false);
	const [showImageModal, setShowImageModal] = useState(false);
	const [vehicleImage, setVehicleImage] = useState(null);
	const [loadingImage, setLoadingImage] = useState(false);
	const [showFullQuotationDetail, setShowFullQuotationDetail] = useState(false);

	const {
		step,
		setStep,
		step1Car,
		setStep1Car,
		step2Car,
		setStep2Car,
		selectedQuotation,
	} = useFilter();

	const {
		reportForm,
		checkCarInfoCompleted,
		checkPersonalInfoCompleted,
		checkQuotationInfoCompleted,
	} = useReport();
	const fetchWithAuth = useAxiosWithAuthInfoAuto();
	const navigate = useNavigate();

	const handleCarInfo = () => {
		if (checkCarInfoCompleted()) {
			setStep1Car(0);
			setStep(0);
			setShowDrawer(false);
		}
	};

	const handlePersonalInfo = () => {
		if (checkPersonalInfoCompleted()) {
			setStep2Car(0);
			setStep(1);
			setShowDrawer(false);
		}
	};

	const handleQuotationInfo = () => {
		if (checkQuotationInfoCompleted()) {
			setStep2Car(1);
			setStep(1);
			setShowDrawer(false);
		}
	};

	// Función para cargar y mostrar la imagen del vehículo
	const handleShowVehicleImage = async () => {
		const carData = reportForm?.carData;
		console.log("Car data for image:", carData);

		if (!carData?.codia) {
			console.warn("No CODIA available for image loading");
			return;
		}

		setLoadingImage(true);
		setShowImageModal(true); // Mostrar modal inmediatamente

		try {
			// Primero intentar usar photo_url si ya está disponible en los datos del modelo
			if (carData.photo_url) {
				console.log("Using photo_url from model data:", carData.photo_url);
				setVehicleImage(carData.photo_url);
				setLoadingImage(false);
				return;
			}

			// Si no tenemos photo_url, intentar obtener la información completa del modelo
			console.log("No photo_url in car data, fetching model info for CODIA:", carData.codia);
			const modelInfo = await get_car_model_info(fetchWithAuth, carData.codia);
			console.log("Model info response:", modelInfo);

			if (modelInfo?.photo_url) {
				console.log("Setting vehicle image from model info:", modelInfo.photo_url);
				setVehicleImage(modelInfo.photo_url);
			} else {
				console.log("No photo_url found in model info, trying photos endpoint");
				// Como último recurso, intentar el endpoint de fotos
				const photos = await get_car_model_photos(fetchWithAuth, carData.codia);
				console.log("Photos response:", photos);

				if (photos && photos.length > 0) {
					console.log("Setting vehicle image from photos endpoint:", photos[0].url);
					setVehicleImage(photos[0].url);
				} else {
					console.log("No photos found, setting null");
					setVehicleImage(null);
				}
			}
		} catch (error) {
			console.error("Error loading vehicle image:", error);
			console.error("Error details:", error.response?.data || error.message);
			setVehicleImage(null);
		} finally {
			setLoadingImage(false);
		}
	};

	// Renderizar información del conductor
	const renderPersonalInfo = () => {
		const personalData = reportForm?.quotationPersonalData;

		// Debug: mostrar qué datos tenemos
		console.log("Personal data in summary:", personalData);
		console.log("Personal info completed:", checkPersonalInfoCompleted());

		if (!personalData || !checkPersonalInfoCompleted()) {
			return (
				<Typo level={306} type="muted">
					Completa la información del conductor para continuar con la cotización.
				</Typo>
			);
		}

		// Formatear fecha de nacimiento
		const formatBirthday = (birthday) => {
			if (!birthday) return "";
			try {
				const date = new Date(birthday);
				return date.toLocaleDateString('es-AR', {
					day: '2-digit',
					month: '2-digit',
					year: 'numeric'
				});
			} catch (error) {
				return birthday;
			}
		};

		// Calcular edad aproximada
		const calculateAge = (birthday) => {
			if (!birthday) return "";
			try {
				const birthDate = new Date(birthday);
				const today = new Date();
				const age = today.getFullYear() - birthDate.getFullYear();
				const monthDiff = today.getMonth() - birthDate.getMonth();

				if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
					return age - 1;
				}
				return age;
			} catch (error) {
				return "";
			}
		};

		const age = calculateAge(personalData.birthday);

		return (
			<div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
				<div style={{ display: "flex", justifyContent: "space-between" }}>
					<Typo level={306} type="black" style={{ fontWeight: "bold" }}>
						{personalData.name} {personalData.lastName}
					</Typo>
					{age && (
						<Typo level={306} type="black">
							{age} años
						</Typo>
					)}
				</div>

				<div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
					<Typo level={307} type="muted" style={{ fontSize: "12px" }}>
						📞 {personalData.telephone}
					</Typo>
					<Typo level={307} type="muted" style={{ fontSize: "12px" }}>
						🎂 {formatBirthday(personalData.birthday)}
					</Typo>
				</div>
			</div>
		);
	};

	// Renderizar información del vehículo
	const renderVehicleInfo = () => {
		const carData = reportForm?.carData;
		const carLocation = reportForm?.carLocation;
		const carFeatures = reportForm?.carSpecialFeatures;

		// Debug: mostrar qué datos tenemos
		console.log("Car data in summary:", carData);
		console.log("Car location in summary:", carLocation);
		console.log("Car features in summary:", carFeatures);
		console.log("Car info completed:", checkCarInfoCompleted());

		// Verificar si tenemos los datos mínimos necesarios
		const hasVehicleData = carData && carData.brandName && carData.modelName && carData.year && carData.codia;

		if (!hasVehicleData) {
			return (
				<Typo level={306} type="muted">
					Selecciona la información de tu vehículo para continuar con la cotización.
				</Typo>
			);
		}

		// Renderizar características especiales
		const renderSpecialFeatures = () => {
			if (!carFeatures) return null;

			const features = [];
			if (carFeatures.hasGNC === true) features.push("GNC");
			if (carFeatures.isZeroKm === true) features.push("0 KM");
			if (carFeatures.hasGarage === true) features.push("Garage");
			if (carFeatures.hasGPS === true) features.push("GPS");

			if (features.length === 0) return null;

			return (
				<div style={{ marginTop: "4px" }}>
					<Typo level={308} type="secondary" style={{ fontSize: "11px" }}>
						{features.join(" • ")}
					</Typo>
				</div>
			);
		};

		// Renderizar ubicación
		const renderLocation = () => {
			if (!carLocation || !carLocation.locality) return null;

			return (
				<div style={{ marginTop: "4px" }}>
					<Typo level={308} type="muted" style={{ fontSize: "11px" }}>
						📍 {carLocation.locality}, {carLocation.province}
					</Typo>
				</div>
			);
		};

		return (
			<div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
				<div style={{ display: "flex", justifyContent: "space-between" }}>
					<Typo level={306} type="black" style={{ fontWeight: "bold" }}>
						{carData.brandName} {carData.modelName}
					</Typo>
					<Typo level={306} type="black">
						{carData.year}
					</Typo>
				</div>
				<Typo level={307} type="muted" style={{ wordBreak: "break-word" }}>
					{carData.description || carData.modelName}
				</Typo>

				{/* Ubicación */}
				{renderLocation()}

				{/* Características especiales */}
				{renderSpecialFeatures()}

				<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "4px" }}>
					<Typo level={307} type="muted">
						CODIA: {carData.codia}
					</Typo>
					{carData.codia && (
						<div
							onClick={handleShowVehicleImage}
							style={{
								display: "flex",
								alignItems: "center",
								gap: "4px",
								cursor: "pointer",
								color: COLORS.Primary,
								fontSize: "12px"
							}}
						>
							<PictureOutlined />
							<span>Ver imagen</span>
						</div>
					)}
				</div>
			</div>
		);
	};

	// Detalle completo de coberturas provisto por la aseguradora (HTML generado
	// por el backend), parseado a una lista simple de items de texto.
	const fullQuotationDetailItems = useMemo(
		() => parseDetailHtmlToItems(selectedQuotation?.detailHtml),
		[selectedQuotation]
	);

	// Renderizar información del plan de cotización seleccionado
	const renderQuotationInfo = () => {
		if (!selectedQuotation || !checkQuotationInfoCompleted()) {

			return (
				<Typo level={306} type="muted">
					Selecciona un plan de cotización para continuar.
				</Typo>
			);
		}

		// selectedQuotation.price ya es el "precio_efectivo" que viene del
		// backend con el descuento ya aplicado. No debe restarse el
		// porcentaje de descuento nuevamente acá.
		const finalPrice = selectedQuotation.price;


		// Coberturas principales a destacar con ícono check/cruz
		const coverageBadges = [
			{ label: "Cristales", value: !!selectedQuotation.hasCrystal },
			{ label: "Granizo", value: !!selectedQuotation.hasHail },
			{ label: "Robo", value: !!selectedQuotation.hasSteal },
			{ label: "Incendio", value: !!selectedQuotation.hasFire },
			{ label: "Auxilio", value: !!selectedQuotation.hasLife },
		];

		return (
			<div style={{ display: "flex", flexDirection: "column", gap: "8px", width: "100%" }}>
				<div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
					{selectedQuotation.companyLogo && (
						<Image width={48} src={selectedQuotation.companyLogo} />
					)}
					<div style={{ display: "flex", flexDirection: "column" }}>
						<Typo level={306} type="black" style={{ fontWeight: "bold" }}>
							{selectedQuotation.companyName}
						</Typo>
						<Typo level={307} type="muted" style={{ wordBreak: "break-word" }}>
							{selectedQuotation.coverage}
						</Typo>
					</div>
				</div>

				<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
					<Typo level={306} type="black">
						$ {numberWithDotAndCommas(Math.round(finalPrice))}
						<Typo level={308} type="muted"> /mes</Typo>
					</Typo>
					{selectedQuotation.valor_asegurado > 0 && (
						<Typo level={308} type="muted">
							Suma asegurada: $ {numberWithDotAndCommas(selectedQuotation.valor_asegurado)}
						</Typo>
					)}
				</div>

				<div
					style={{
						display: "flex",
						flexWrap: "wrap",
						gap: "10px",
						marginTop: "4px",
					}}
				>
					{coverageBadges.map(({ label, value }) => (
						<div
							key={label}
							style={{ display: "flex", alignItems: "center", gap: "4px" }}
						>
							{value ? (
								<CheckCircleOutlined style={{ color: COLORS.Success, fontSize: 12 }} />
							) : (
								<CloseCircleOutlined style={{ color: COLORS.Danger, fontSize: 12 }} />
							)}
							<Typo level={308} type="muted" style={{ fontSize: "11px" }}>
								{label}
							</Typo>
						</div>
					))}
				</div>

				{fullQuotationDetailItems.length > 0 && (
					<div style={{ marginTop: "4px" }}>
						<div
							onClick={(e) => {
								e.stopPropagation();
								setShowFullQuotationDetail((prev) => !prev);
							}}
							style={{
								display: "flex",
								alignItems: "center",
								gap: "4px",
								cursor: "pointer",
								color: COLORS.Secondary,
								fontSize: "12px",
							}}
						>
							<Typo level={308} type="secondary">
								Ver detalle completo
							</Typo>
							<BiChevronDown
								size={16}
								color={COLORS.Secondary}
								style={{
									transform: showFullQuotationDetail
										? "rotate(180deg)"
										: "rotate(0deg)",
									transition: "transform 0.2s ease-in-out",
								}}
							/>
						</div>

						{showFullQuotationDetail && (
							<ul
								style={{
									listStyle: "none",
									margin: "8px 0 0 0",
									padding: 0,
									display: "flex",
									flexDirection: "column",
									gap: "6px",
								}}
							>
								{fullQuotationDetailItems.map((detailItem, idx) => (
									<li
										key={idx}
										style={{
											display: "flex",
											alignItems: "flex-start",
											gap: "6px",
										}}
									>
										<CheckCircleOutlined
											style={{ fontSize: 12, marginTop: 3, color: COLORS.Success }}
										/>
										<Typo level={308} type="muted" style={{ fontSize: "11px" }}>
											{detailItem}
										</Typo>
									</li>
								))}
							</ul>
						)}
					</div>
				)}
			</div>
		);
	};

	return (
		<Layout>

			<SellerHeader
				setShowDrawer={setShowDrawer}
				showDrawer={showDrawer}
				clientPhotoUrl={SystemLogo}
			/>
			<Layout.Main>{children}</Layout.Main>
			<Drawer
				title="Resumen"
				// headerStyle={{ display: "none" }}
				placement="right"
				onClose={() => setShowDrawer(false)}
				open={showDrawer}
				zIndex={998}
			>
				<Aside>
					{!true ? (
						"Loading..."
					) : (
						<Aside.Actions>
							<Aside.Separator>
								<PermissionCheckVisibility permission={true}>
									<Aside.Action
										border={true}
										onClick={handleCarInfo}
										cursor={checkCarInfoCompleted()}
									>
										<Aside.Top>
											<Aside.Container>
												<CarOutlined
													style={{
														color: checkCarInfoCompleted()
															? COLORS.Black
															: COLORS.Grey,
													}}
												/>
												<Typo
													type={checkCarInfoCompleted() ? "black" : "muted"}
													level={6}
												>
													Información del vehículo
												</Typo>
											</Aside.Container>
											<Aside.Container>
												<BiEdit
													color={
														checkCarInfoCompleted()
															? COLORS.Secondary
															: COLORS.Grey
													}
												/>
												<Typo
													type={checkCarInfoCompleted() ? "secondary" : "muted"}
												>
													Modificar
												</Typo>
											</Aside.Container>
										</Aside.Top>
										<Aside.Container>
											{renderVehicleInfo()}
										</Aside.Container>
									</Aside.Action>
								</PermissionCheckVisibility>
								<Aside.Action> </Aside.Action>
								<PermissionCheckVisibility permission={true}>
									<Aside.Action
										border={true}
										onClick={handlePersonalInfo}
										cursor={checkPersonalInfoCompleted()}
									>
										<Aside.Top>
											<Aside.Container>
												<UserOutlined
													style={{
														color: checkPersonalInfoCompleted()
															? COLORS.Black
															: COLORS.Grey,
													}}
												/>
												<Typo
													level={6}
													type={
														checkPersonalInfoCompleted() ? "black" : "muted"
													}
												>
													Información del conductor
												</Typo>
											</Aside.Container>
											<Aside.Container>
												<BiEdit
													color={
														checkPersonalInfoCompleted()
															? COLORS.Secondary
															: COLORS.Grey
													}
												/>
												<Typo
													type={
														checkPersonalInfoCompleted() ? "secondary" : "muted"
													}
												>
													Modificar
												</Typo>
											</Aside.Container>
										</Aside.Top>
										<Aside.Container>
											{renderPersonalInfo()}
										</Aside.Container>
									</Aside.Action>
								</PermissionCheckVisibility>
								<Aside.Action> </Aside.Action>
								<PermissionCheckVisibility permission={true}>
									<Aside.Action
										border={true}
										onClick={handleQuotationInfo}
										cursor={checkQuotationInfoCompleted()}
									>
										<Aside.Top>
											<Aside.Container>
												<BsCardList
													style={{
														color: checkQuotationInfoCompleted()
															? COLORS.Black
															: COLORS.Grey,
													}}
												/>
												<Typo
													level={6}
													type={
														checkQuotationInfoCompleted() ? "black" : "muted"
													}
												>
													Plan seleccionado
												</Typo>
											</Aside.Container>
											<Aside.Container>
												<BiEdit
													color={
														checkQuotationInfoCompleted()
															? COLORS.Secondary
															: COLORS.Grey
													}
												/>
												<Typo
													type={
														checkQuotationInfoCompleted()
															? "secondary"
															: "muted"
													}
												>
													Modificar
												</Typo>
											</Aside.Container>
										</Aside.Top>
										<Aside.Container>
											{renderQuotationInfo()}
										</Aside.Container>

									</Aside.Action>
								</PermissionCheckVisibility>
							</Aside.Separator>
							<Aside.Action> </Aside.Action>
						</Aside.Actions>
					)}
				</Aside>
			</Drawer>

			{/* Modal para mostrar imagen del vehículo */}
			<Modal
				title={
					<div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
						<PictureOutlined />
						<span>Imagen del vehículo</span>
					</div>
				}
				open={showImageModal}
				onCancel={() => {
					setShowImageModal(false);
					setVehicleImage(null);
					setLoadingImage(false);
				}}
				footer={null}
				width={600}
				centered
			>
				<div style={{ textAlign: "center", padding: "20px 0" }}>
					{loadingImage ? (
						<div style={{ padding: "40px" }}>
							<div style={{ fontSize: "16px", marginBottom: "16px" }}>Cargando imagen...</div>
							<div style={{ fontSize: "12px", color: "#999" }}>
								Obteniendo foto del modelo desde InfoAuto
							</div>
						</div>
					) : vehicleImage ? (
						<div>
							<img
								src={vehicleImage}
								alt="Imagen del vehículo"
								style={{
									maxWidth: "100%",
									maxHeight: "400px",
									objectFit: "contain",
									borderRadius: "8px",
									border: "1px solid #f0f0f0"
								}}
								onLoad={() => console.log("Image loaded successfully:", vehicleImage)}
								onError={(e) => {
									console.error("Image failed to load:", vehicleImage);
									e.target.style.display = "none";
									e.target.nextSibling.style.display = "block";
								}}
							/>
							<div style={{ display: "none", padding: "40px", color: "#999" }}>
								<PictureOutlined style={{ fontSize: "48px", marginBottom: "16px" }} />
								<div>No se pudo cargar la imagen</div>
								<div style={{ fontSize: "12px", marginTop: "8px" }}>
									La URL de la imagen no es válida o no está disponible
								</div>
							</div>
						</div>
					) : (
						<div style={{ padding: "40px", color: "#999" }}>
							<PictureOutlined style={{ fontSize: "48px", marginBottom: "16px" }} />
							<div>No hay imagen disponible para este modelo</div>
							<div style={{ fontSize: "12px", marginTop: "8px" }}>
								Puedes verificar el modelo seleccionado con la información mostrada
							</div>
						</div>
					)}
					{reportForm?.carData && (
						<div style={{ marginTop: "16px", padding: "12px", backgroundColor: "#f5f5f5", borderRadius: "6px" }}>
							<Typo level={306} type="black" style={{ fontWeight: "bold" }}>
								{reportForm.carData.brandName} {reportForm.carData.modelName} {reportForm.carData.year}
							</Typo>
							<Typo level={307} type="muted" style={{ wordBreak: "break-word" }}>
								{reportForm.carData.description}
							</Typo>
							<Typo level={308} type="muted" style={{ marginTop: "4px" }}>
								CODIA: {reportForm.carData.codia}
							</Typo>
						</div>
					)}
				</div>
			</Modal>

			<FooterContainer img={SystemLogo}></FooterContainer>
		</Layout>
	);
}
